import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenant } from '@/hooks/useTenant';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// Configuración completa de idiomas disponibles
const AVAILABLE_LANGUAGES = {
  es: {
    code: 'es',
    name: 'Español',
    nativeName: 'Español',
    flag: '🇪🇸',
    region: 'España',
    enabled: true
  },
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    region: 'United States',
    enabled: true
  },
  pt: {
    code: 'pt',
    name: 'Português',
    nativeName: 'Português',
    flag: '🇧🇷',
    region: 'Brasil',
    enabled: true
  },
  fr: {
    code: 'fr',
    name: 'Français',
    nativeName: 'Français',
    flag: '🇫🇷',
    region: 'France',
    enabled: false // Preparado para futuro
  },
  de: {
    code: 'de',
    name: 'Deutsch',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    region: 'Deutschland',
    enabled: false // Preparado para futuro
  },
  it: {
    code: 'it',
    name: 'Italiano',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    region: 'Italia',
    enabled: false // Preparado para futuro
  },
  zh: {
    code: 'zh',
    name: 'Chinese',
    nativeName: '中文',
    flag: '🇨🇳',
    region: 'China',
    enabled: false // Preparado para futuro
  },
  ja: {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵',
    region: 'Japan',
    enabled: false // Preparado para futuro
  }
};

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'buttons' | 'compact';
  showFlags?: boolean;
  showRegion?: boolean;
  className?: string;
}

export function LanguageSwitcher({ 
  variant = 'dropdown',
  showFlags = true,
  showRegion = false,
  className 
}: LanguageSwitcherProps) {
  const { currentLanguage, changeLanguage } = useLanguage();
  const { config } = useTenant();
  const [isOpen, setIsOpen] = useState(false);

  // Obtener idiomas habilitados para el tenant actual
  const enabledLanguages = config?.languages || ['es', 'en'];
  
  // Filtrar idiomas disponibles según configuración del tenant
  const availableLanguages = enabledLanguages
    .map(code => AVAILABLE_LANGUAGES[code as keyof typeof AVAILABLE_LANGUAGES])
    .filter(Boolean);

  // Si solo hay un idioma disponible, no mostrar el selector
  if (availableLanguages.length <= 1) {
    return null;
  }

  const currentLang = AVAILABLE_LANGUAGES[currentLanguage as keyof typeof AVAILABLE_LANGUAGES];

  const handleLanguageChange = (langCode: string) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  // Variante de botones (para móvil o cuando hay pocos idiomas)
  if (variant === 'buttons') {
    return (
      <div className={cn("flex items-center space-x-1", className)}>
        {availableLanguages.map((lang) => (
          <Button
            key={lang.code}
            variant={currentLanguage === lang.code ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleLanguageChange(lang.code)}
            className={cn(
              "px-2 py-1 text-xs transition-all",
              currentLanguage === lang.code && "bg-primary text-primary-foreground shadow-sm"
            )}
          >
            {showFlags && <span className="mr-1">{lang.flag}</span>}
            {lang.code.toUpperCase()}
          </Button>
        ))}
      </div>
    );
  }

  // Variante compacta (solo bandera y código)
  if (variant === 'compact') {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "px-2 py-1 h-8 min-w-[60px] flex items-center justify-center",
              "hover:bg-gray-100 transition-colors",
              className
            )}
          >
            {showFlags && <span className="mr-1 text-base">{currentLang?.flag}</span>}
            <span className="text-xs font-medium tracking-wide">{currentLanguage.toUpperCase()}</span>
            <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 mt-1">
          <DropdownMenuLabel className="text-xs font-medium text-gray-500">
            Seleccionar idioma
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {availableLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={cn(
                "flex items-center justify-between py-2 cursor-pointer",
                currentLanguage === lang.code && "bg-gray-50"
              )}
            >
              <div className="flex items-center">
                {showFlags && <span className="mr-2 text-base">{lang.flag}</span>}
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{lang.nativeName}</span>
                  <span className="text-xs text-gray-500">{lang.code.toUpperCase()}</span>
                </div>
              </div>
              {currentLanguage === lang.code && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Variante dropdown (por defecto)
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={cn("flex items-center space-x-2 min-w-[120px]", className)}
        >
          <Globe className="h-4 w-4" />
          <div className="flex items-center space-x-1">
            {showFlags && currentLang && (
              <span className="text-sm">{currentLang.flag}</span>
            )}
            <span className="text-sm font-medium">
              {currentLang?.name || currentLanguage.toUpperCase()}
            </span>
          </div>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center">
          <Globe className="h-4 w-4 mr-2" />
          Seleccionar Idioma
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Idiomas habilitados */}
        {availableLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex items-center justify-between py-3 cursor-pointer"
          >
            <div className="flex items-center space-x-3">
              {showFlags && (
                <span className="text-lg">{lang.flag}</span>
              )}
              <div className="flex flex-col">
                <span className="text-sm font-medium">{lang.nativeName}</span>
                {showRegion && (
                  <span className="text-xs text-gray-500">{lang.region}</span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {currentLanguage === lang.code && (
                <>
                  <Badge variant="secondary" className="text-xs">
                    Actual
                  </Badge>
                  <Check className="h-4 w-4 text-primary" />
                </>
              )}
            </div>
          </DropdownMenuItem>
        ))}

        {/* Idiomas próximamente (si hay alguno configurado pero no habilitado) */}
        {Object.values(AVAILABLE_LANGUAGES).some(lang => !lang.enabled) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-gray-500">
              Próximamente
            </DropdownMenuLabel>
            {Object.values(AVAILABLE_LANGUAGES)
              .filter(lang => !lang.enabled)
              .slice(0, 3) // Mostrar solo los primeros 3
              .map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  disabled
                  className="flex items-center justify-between py-2 opacity-50"
                >
                  <div className="flex items-center space-x-3">
                    {showFlags && (
                      <span className="text-sm">{lang.flag}</span>
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm">{lang.nativeName}</span>
                      {showRegion && (
                        <span className="text-xs text-gray-400">{lang.region}</span>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Pronto
                  </Badge>
                </DropdownMenuItem>
              ))}
          </>
        )}

        <DropdownMenuSeparator />
        <div className="px-2 py-1">
          <p className="text-xs text-gray-500 text-center">
            {availableLanguages.length} de {Object.keys(AVAILABLE_LANGUAGES).length} idiomas disponibles
          </p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}